<template>
  <v-card>
    <v-toolbar color="secondary" density="default">
      <v-toolbar-title class="text-white">
        {{ $t("strings.dashboard-kpi-visualization-chart") }}
        <div class="text-caption">
          {{ $t("strings.dashboard-suggestion") }}
        </div>
      </v-toolbar-title>
    </v-toolbar>
    <v-card-text>
      <v-sheet :max-height="innerChartHeight" class="d-flex justify-center">
        <canvas ref="chartRef"></canvas>
      </v-sheet>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed } from "vue";
import { Chart, registerables } from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { applyTransparencyToDatasets } from "@/utils/chartUtils.js";

Chart.register(...registerables, annotationPlugin);

const props = defineProps({
  type: String,
  data: Array,
  labels: Array,
  size: Object,
  targetValue: [Number, String],
  cardHeight: String,
  isQualitative: Boolean,
  horizontal: Boolean,
});

const innerChartHeight = computed(() => {
  //Container - title - PaddingTop&Bottom
  return "calc(" + props.cardHeight + " - 64px - 32px - 2px)";
  // return props.cardHeight;
});

const chartRef = ref(null);
let chartInstance = null;

onMounted(() => {
  if (chartRef.value) {
    renderChart();
  }

  // Aggiungi il listener per il resize
  window.addEventListener("resize", updateChart);
});
onBeforeUnmount(() => {
  if (chartInstance) {
    chartInstance.destroy();
  }
  // Remove resize listener
  window.removeEventListener("resize", updateChart);
});

watch(
  () => props.type,
  () => {
    if (chartInstance) {
      chartInstance.destroy();
    }
    renderChart();
  },
);

watch(
  () => props.horizontal,
  () => {
    if (chartInstance) {
      chartInstance.destroy();
    }
    renderChart();
  },
);

watch(
  () => props.cardHeight,
  () => {
    updateChart();
  },
);
function updateChart() {
  if (chartInstance) {
    chartInstance.destroy();
  }
  renderChart();
}

function renderChart() {
  if (!chartRef.value) {
    console.warn("Chart canvas not found");
    return;
  }

  // Apply transparency to dataset colors
  const dataWithTransparency = {
    labels: props.labels,
    datasets: applyTransparencyToDatasets(props.data)
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: props.type !== "doughnut" && props.type !== "polarArea" && props.type !== "radar",
    indexAxis: props.horizontal ? 'y' : 'x',
    plugins: {
      legend: {
        position: props.type !== "doughnut" && props.type !== "polarArea" ? "top" : "right",
      },
      annotation: {
        annotations:
          !props.isQualitative && props.type !== "doughnut"
            ? {
                line1: {
                  type: "line",
                  ...(props.horizontal 
                    ? { xMin: props.targetValue, xMax: props.targetValue }
                    : { yMin: props.targetValue, yMax: props.targetValue }
                  ),
                  borderColor: "#4CAF50",
                  borderWidth: 2,
                  label: {
                    content: `Target: ${props.targetValue}`,
                    enabled: true,
                    position: "end",
                  },
                },
              }
            : {},
      },
      tooltip: {
        callbacks: props.isQualitative ? {
          label: function(context) {
            const label = context.label || '';
            const value = props.horizontal 
              ? context.parsed.x 
              : (context.parsed.y || context.parsed.r || context.parsed);
            const isTarget = label === props.targetValue;
            return `${label}: ${value}${isTarget ? ' (Target)' : ''}`;
          }
        } : undefined,
      },
    },
    scales: props.type === "doughnut" || props.type === "polarArea" || props.type === "radar" ? undefined : {
      x: {
        title: {
          display: props.isQualitative && !props.horizontal,
          text: props.isQualitative ? 'Categorie' : undefined,
        },
      },
      y: {
        title: {
          display: props.isQualitative,
          text: props.horizontal ? 'Categorie' : 'Frequenza',
        },
        beginAtZero: true,
      },
    },
  };

  chartInstance = new Chart(chartRef.value, {
    type: props.type === "area" ? "line" : props.type,
    data: dataWithTransparency,
    options: chartOptions,
  });
}
</script>
